import { useState, useEffect } from 'react';
import { apiInstance } from 'lib/utils/axios';
import { API_ROUTES } from 'lib/api-routes';
import { ChartData, ResultObject } from 'lib/models/graphdata';

type Demographics = {
  [key: string]: number;
};

function useChartData(id: string) {
  const [memberDetailsArray, setMemberDetailsArray] = useState<ChartData[]>([]);
  const [womenPercentage, setWomenPercentage] = useState(0);
  const [studyYearPercentage, setstudyYearPercentage] = useState(0);
  const [graduationPercentage, setGraduationPercentage] = useState(0);
  const [maxFrequencyGraduationYear, setMaxFrequencyGraduationYear] =
    useState('');
  const [maxFrequencyStudyYear, setMaxFrequencyStudyYear] = useState('');

  function yearWithMaxFrequency(
    demographics: Demographics,
    isGraduationYear: boolean
  ) {
    let maxYear = null;
    let maxCount = 0;

    for (const key in demographics) {
      let count = 0;
      if (
        isGraduationYear &&
        new Date(key).getFullYear() > new Date().getFullYear()
      ) {
        count = demographics[key];
      }
      if (!isGraduationYear) {
        count = demographics[key];
      }

      if (count > maxCount) {
        maxCount = count;
        maxYear = key;
      }
    }

    return { year: maxYear, count: maxCount };
  }

  useEffect(() => {
    const getMembersDetails = async () => {
      try {
        if (id) {
          const res = await apiInstance.get(
            `${API_ROUTES.STUDENT_SOCIETIES}/${id}/members_demographic_details`
          );
          const { gender, study_year, graduation_year } = res.data || {};

          const percentageWomen =
            (gender.demographics.Woman || 0) /
            gender.members_with_details_count;
          setWomenPercentage(Math.floor(percentageWomen * 100));
          const maxFrequencyYear = yearWithMaxFrequency(
            study_year.demographics,
            false
          );
          setMaxFrequencyStudyYear(maxFrequencyYear?.year || '');
          setstudyYearPercentage(
            Math.floor(
              (maxFrequencyYear.count / study_year.members_with_details_count) *
                100
            ) || 0
          );
          const maxGraduationYear = yearWithMaxFrequency(
            graduation_year.demographics,
            true
          );

          const currentYear = new Date().getFullYear();
          const graduatedYears = Object.entries(graduation_year.demographics)
            .filter(([name]) => {
              const date = new Date(name);
              return date.getFullYear() <= currentYear;
            })
            .reduce((acc, [, value]) => acc + (value as number), 0);

          if (maxGraduationYear?.count > graduatedYears) {
            setMaxFrequencyGraduationYear(maxGraduationYear?.year || '');
          } else if (maxGraduationYear.count > 0 && graduatedYears > 0) {
            setMaxFrequencyGraduationYear('graduated');
          }
          const maxGraduationYearCount =
            maxGraduationYear.count > graduatedYears
              ? maxGraduationYear.count
              : graduatedYears;

          setGraduationPercentage(
            Math.floor(
              (maxGraduationYearCount /
                graduation_year.members_with_details_count) *
                100
            )
          );
          setMemberDetailsArray(
            Object.entries(res.data).map(
              ([title, value]) => ({ title, value } as ChartData)
            )
          );
        }
      } catch (error) {
        console.error('Error fetching member details:', error);
      }
    };

    getMembersDetails();
  }, [id]);

  function objectToArrayWithTopAndOthers(
    topCount: number,
    title: string,
    inputObject?: Record<string, number>
  ): ResultObject[] {
    const sortedEntries = Object.entries(inputObject || {}).sort(
      (a, b) => b[1] - a[1]
    );
    const totalValue = sortedEntries.reduce((acc, [, value]) => acc + value, 0);
    const topEntries = sortedEntries.slice(0, topCount);
    const remainingEntries = sortedEntries.slice(topCount);
    if (title === 'graduation_year') {
      const currentYear = new Date().getFullYear();
      const graduatedYears = sortedEntries
        .filter(([name]) => {
          const date = new Date(name);
          return date.getFullYear() <= currentYear;
        })
        .reduce((acc, [, value]) => acc + value, 0);

      const graduatedYearsDetails = [
        {
          name: 'Graduated',
          value: graduatedYears,
          percentage: parseFloat(
            ((graduatedYears / totalValue) * 100).toFixed(2)
          ),
        },
      ];

      const otherYearsDetails = remainingEntries
        .filter(([name]) => {
          const date = new Date(name);
          return date.getFullYear() > currentYear;
        })
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .sort(
          (a, b) => new Date(a[0]).getFullYear() - new Date(b[0]).getFullYear()
        )
        .map(([name, value]) => ({
          name: new Date(name).getFullYear().toString(),
          value,
          percentage: ((value / totalValue) * 100).toFixed(2),
        }));
      const remainingOtherDetails = remainingEntries
        .filter(([name]) => {
          const date = new Date(name);
          return (
            date.getFullYear() <= currentYear ||
            otherYearsDetails.some(
              ({ name: topYear }) => topYear === date.getFullYear().toString()
            )
          );
        })
        .map(([name, value]) => ({
          name: new Date(name).getFullYear().toString(),
          value,
          percentage: ((value / totalValue) * 100).toFixed(2),
        }));
      if (
        totalValue -
          graduatedYears -
          otherYearsDetails.reduce((acc, { value }) => acc + value, 0) >
        0
      ) {
        return [
          ...graduatedYearsDetails,
          ...otherYearsDetails,
          {
            name: 'Other',
            value:
              totalValue -
              graduatedYears -
              otherYearsDetails.reduce((acc, { value }) => acc + value, 0),
            details: remainingOtherDetails,
          },
        ];
      } else {
        return [...graduatedYearsDetails, ...otherYearsDetails];
      }
    }

    if (title === 'study_year') {
      const validEntries = sortedEntries.filter(
        ([name, value]) => name !== '0' && value > 0
      );
      const sortedObjects = validEntries
        .map(([name, value]) => ({
          name: parseInt(name, 10),
          value,
        }))
        .sort((a, b) => a.name - b.name);
      const topObjects = sortedObjects.map(({ name, value }) => {
        const customName = `${
          name === 1
            ? '1st'
            : name === 2
            ? '2nd'
            : name === 3
            ? '3rd'
            : `${name}th`
        } Year`;

        return {
          name: customName,
          value,
        };
      });

      return [...topObjects];
    }
    const topObjects = topEntries.map(([name, value]) => {
      let customName = name;

      switch (name) {
        case 'job':
          customName = 'Job';
          break;
        case 'internship':
          customName = 'Internship';
          break;
        case 'event':
          customName = 'Event';
          break;
        default:
          break;
      }

      return {
        name: customName,
        value,
      };
    });

    const othersDetails = remainingEntries.map(([name, value]) => {
      const customName = name;

      return {
        name: customName,
        value,
        percentage: ((value / totalValue) * 100).toFixed(2),
      };
    });
    const othersValue = othersDetails.reduce(
      (acc, { value }) => acc + value,
      0
    );
    const othersObject = {
      name: 'Others',
      value: othersValue,
      details: othersDetails,
    };
    if (sortedEntries.length > 4) {
      return [...topObjects, othersObject];
    } else {
      return [...topObjects];
    }
  }

  return {
    memberDetailsArray,
    objectToArrayWithTopAndOthers,
    womenPercentage,
    studyYearPercentage,
    graduationPercentage,
    maxFrequencyGraduationYear,
    maxFrequencyStudyYear,
  };
}

export default useChartData;
